import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import countriesData from "../../utils/countries-min.json";

export const CitizenshipInput = forwardRef(({
  translation,
  citizenshipValue,
  setCitizenshipValue,
  setCitizenshipCode,
  onComplete,
  readOnly = false,
  onSelect,
  autoFocus = false,
  onFocus,
  onBlur,
  hasError = false,
  onDropdownToggle = null
}, ref) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
      setShowSuggestions(true);
    }
  }));

  useEffect(() => {
    if (onDropdownToggle) {
      // Trigger blur immediately when input is focused (showSuggestions is true)
      // regardless of search results or typing
      onDropdownToggle(showSuggestions);
    }
  }, [showSuggestions, onDropdownToggle]);

  // Enhanced mobile focus and keyboard behavior
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      // Multiple focus attempts with different delays to ensure keyboard opens
      const focusAttempts = [
        () => {
          inputRef.current?.focus();
          setShowSuggestions(true);
        },
        () => {
          inputRef.current?.click();
          inputRef.current?.focus();
          setShowSuggestions(true);
        },
        () => {
          // Force keyboard on mobile by simulating touch
          const event = new Event('touchstart', { bubbles: true });
          inputRef.current?.dispatchEvent(event);
      inputRef.current?.focus();
      setShowSuggestions(true);
        }
      ];

      // Execute focus attempts with increasing delays
      focusAttempts.forEach((attempt, index) => {
        setTimeout(() => {
          attempt();
          
          // Scroll to input after each attempt
          if (inputRef.current) {
            inputRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'nearest'
            });
          }
        }, 100 * (index + 1)); // 100ms, 200ms, 300ms delays
      });
    }
  }, [autoFocus]);

  const handleInputChange = (e) => {
    const input = e.target.value;
    setCitizenshipValue(input);

    if (!input.trim()) {
      setFilteredCountries([]);
      setShowSuggestions(false);
      return;
    }

    const results = countriesData.filter((country) =>
      country.name.common.toLowerCase().includes(input.toLowerCase())
    );

    setFilteredCountries(results);
    if (!readOnly) {
      setShowSuggestions(true);
    }
  };

  const handleSelection = (country) => {
    setCitizenshipValue(country.name.common);
    setCitizenshipCode(country.cca2, country.name.common);
    setShowSuggestions(false);

    if (onSelect) onSelect(country.cca2, country.name.common);
  };

  const handleFocus = (e) => {
    if (!readOnly) {
      setShowSuggestions(true);
      
      // Improved mobile scroll on focus
      const scrollToInput = () => {
        if (inputRef.current) {
          inputRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          });
          
          // Additional scroll after keyboard appears
          setTimeout(() => {
            inputRef.current?.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start',
              inline: 'nearest'
            });
          }, 300);
        }
      };
      
      scrollToInput();
    }
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e) => {
    setTimeout(() => setShowSuggestions(false), 200);
    if (onBlur) onBlur(e);
  };

  return (
    <div ref={wrapperRef} id="citizenship-container" style={{ position: "relative", width: "100%" }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        background: "#f8f9fa",
        borderRadius: '8px',
        height: '56px',
        cursor: readOnly ? "default" : "pointer",
        border: hasError ? '2px solid #dc3545' : 'none',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{
          paddingLeft: '15px',
          display: 'flex',
          alignItems: 'center'
        }}>
          <img 
            src={require("../../assets/svg/personOutLine.svg").default}
            alt="person"
            style={{ 
              width: 24, 
              height: 24,
              filter: hasError ? 'brightness(0) saturate(100%) invert(21%) sepia(77%) saturate(4895%) hue-rotate(347deg) brightness(95%) contrast(86%)' : 'none'
            }} 
          />
        </div>
        <input
          ref={inputRef}
          id="citizenship-input-field"
          type="text"
          className={`form-control citizenship-input ${hasError ? 'error-placeholder' : ''}`}
          value={citizenshipValue || ''}
          onChange={handleInputChange}
          placeholder={translation("Nationality")}
          onFocus={handleFocus}
          onBlur={handleBlur}
          readOnly={readOnly}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          style={{
            padding: "12px 16px",
            paddingLeft: "8px",
            border: 'none',
            background: "transparent",
            cursor: readOnly ? "default" : "text",
            height: '56px',
            fontSize: '18px',
            fontWeight: '500',
            width: '100%',
            color: hasError ? '#dc3545' : '#003663',
            '&::placeholder': {
              color: hasError ? '#dc3545' : '#6c757d',
              opacity: 1
            }
          }}
        />
      </div>

      {showSuggestions && filteredCountries.length > 0 && (
        <ul
          className="list-group position-absolute w-100 mt-1 bg-white shadow"
          style={{ 
            zIndex: 9999,
            maxHeight: '60vh',
            overflowY: 'auto',
            borderRadius: '8px'
          }}
        >
          {filteredCountries.map((country) => (
            <li
              key={country.cca2}
              className="list-group-item list-group-item-action"
              onClick={() => handleSelection(country)}
              style={{ 
                cursor: "pointer",
                padding: '12px 16px',
                borderBottom: '1px solid #eef1f5'
              }}
            >
              {country.name.common} ({country.cca2})
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});
